import { useEffect, useId, useRef, useState } from 'react';
import { titleCase } from '../lib/format.js';

// The small building blocks the rest of the interface is assembled from.

export const Spinner = ({ label = 'Loading' }) => (
  <div className="spinner-wrap" role="status">
    <span className="spinner" aria-hidden="true" />
    <span className="sr-only">{label}</span>
  </div>
);

export const EmptyState = ({ title, message, action }) => (
  <div className="empty-state">
    <h3>{title}</h3>
    {message ? <p>{message}</p> : null}
    {action}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="empty-state error">
    <h3>That did not work</h3>
    <p>{message}</p>
    {onRetry ? (
      <button type="button" className="btn btn-outline" onClick={onRetry}>
        Try again
      </button>
    ) : null}
  </div>
);

const TONE_BY_STATUS = {
  available: 'good',
  approved: 'good',
  verified: 'good',
  resolved: 'good',
  pending: 'warn',
  under_review: 'warn',
  submitted: 'info',
  open: 'warn',
  draft: 'muted',
  withdrawn: 'muted',
  dismissed: 'muted',
  not_required: 'muted',
  unsubmitted: 'muted',
  rented: 'good',
  rejected: 'bad',
  occupied: 'good',
  unavailable: 'muted',
  sold: 'info'
};

export const StatusBadge = ({ status, label }) => (
  <span className={`badge badge-${TONE_BY_STATUS[status] || 'muted'}`}>{label || titleCase(status)}</span>
);

export const Field = ({ label, hint, error, children, required }) => (
  <label className={`field ${error ? 'field-error' : ''}`}>
    <span className="field-label">
      {label}
      {required ? <span className="field-required" aria-hidden="true"> *</span> : null}
    </span>
    {children}
    {error ? <span className="field-message">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
  </label>
);

/**
 * A password box with a control to read back what has been typed.
 *
 * Typing a password blind on a phone keyboard is how people end up locked out of
 * their own account, so this lets them check it. It starts hidden and only shows
 * on a deliberate press - somebody signing in on a shared phone should not have
 * their password on screen by default.
 *
 * Unlike Field, the label is tied to the input by id rather than by wrapping it.
 * Wrapping would fold the button's wording into the input's own name, so a
 * screen reader would announce the box as "Password Show".
 */
export const PasswordField = ({ label, hint, error, required, id, ...inputProps }) => {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const [shown, setShown] = useState(false);
  const action = shown ? 'Hide password' : 'Show password';

  return (
    <div className={`field ${error ? 'field-error' : ''}`}>
      <label className="field-label" htmlFor={fieldId}>
        {label}
        {required ? <span className="field-required" aria-hidden="true"> *</span> : null}
      </label>

      <div className="password-field">
        <input id={fieldId} type={shown ? 'text' : 'password'} required={required} {...inputProps} />
        <button
          type="button"
          className="password-toggle"
          onClick={() => setShown((current) => !current)}
          aria-pressed={shown}
          aria-label={action}
          title={action}
        >
          {shown ? 'Hide' : 'Show'}
        </button>
      </div>

      {error ? <span className="field-message">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </div>
  );
};

export const Modal = ({ title, children, onClose, footer, wide }) => {
  const panel = useRef(null);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={`modal ${wide ? 'modal-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
      </div>
    </div>
  );
};

export const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;

  // Show a window around the current page so long result sets stay usable.
  const numbers = [];
  const from = Math.max(1, page - 2);
  const to = Math.min(totalPages, from + 4);
  for (let i = from; i <= to; i += 1) numbers.push(i);

  return (
    <nav className="pagination" aria-label="Pages">
      <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        Previous
      </button>
      {from > 1 ? <span className="pagination-gap">...</span> : null}
      {numbers.map((number) => (
        <button
          key={number}
          type="button"
          className={`page-btn ${number === page ? 'is-current' : ''}`}
          aria-current={number === page ? 'page' : undefined}
          onClick={() => onChange(number)}
        >
          {number}
        </button>
      ))}
      {to < totalPages ? <span className="pagination-gap">...</span> : null}
      <button
        type="button"
        className="btn btn-ghost"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        Next
      </button>
    </nav>
  );
};

// A plain table with a sticky head, used by all three dashboards.
/**
 * `footer` takes a `<tr>` and lands in a `<tfoot>`, for a totals line that has
 * to stay a table row rather than a paragraph underneath - the figures only
 * read as a sum if they sit in the same columns they add up.
 */
export const DataTable = ({ columns, rows, empty, rowKey = (row) => row.id, onRowClick, footer = null }) => {
  if (rows.length === 0) {
    return <EmptyState title={empty?.title || 'Nothing here yet'} message={empty?.message} />;
  }

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={column.width ? { width: column.width } : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={onRowClick ? 'is-clickable' : ''}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((column) => (
                <td key={column.key} data-label={column.header}>
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer ? <tfoot>{footer}</tfoot> : null}
      </table>
    </div>
  );
};

export const Avatar = ({ name, url, size = 40 }) => {
  const letters = (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return url ? (
    <img className="avatar" src={url} alt="" width={size} height={size} style={{ width: size, height: size }} />
  ) : (
    <span className="avatar avatar-letters" style={{ width: size, height: size, fontSize: size / 2.6 }}>
      {letters}
    </span>
  );
};

export const Confirm = ({ title, message, confirmLabel = 'Confirm', tone = 'danger', onConfirm, onClose, busy }) => (
  <Modal
    title={title}
    onClose={onClose}
    footer={
      <>
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={`btn btn-${tone}`} onClick={onConfirm} disabled={busy}>
          {busy ? 'Working...' : confirmLabel}
        </button>
      </>
    }
  >
    <p>{message}</p>
  </Modal>
);

/**
 * Confirm, but the person has to say why.
 *
 * Used wherever the answer is written down afterwards - a waived charge, a
 * cancelled bill, a customer taken off a meter - so the reason is part of the
 * record rather than something only the person clicking knows. Field errors
 * come back off `ApiError.details`, so a server-side `{ reason: '...' }` lands
 * on the box itself.
 */
export const ReasonModal = ({ title, lead, label, confirmLabel, tone = 'primary', suggestions = [], onSubmit, onClose }) => {
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit(reason.trim());
    } catch (error) {
      if (error?.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="reason-form" className={`btn btn-${tone}`} disabled={busy}>
            {busy ? 'Saving...' : confirmLabel}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}
      <p className="modal-lead">{lead}</p>
      <form id="reason-form" className="stack" onSubmit={submit}>
        <Field label={label} error={errors.reason} required>
          <input type="text" value={reason} onChange={(event) => setReason(event.target.value)} required />
        </Field>
        {/* The usual reasons, one tap each; the box stays editable. */}
        {suggestions.length ? (
          <div className="reason-chips" role="group" aria-label="Common reasons">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className={`reason-chip ${reason === suggestion ? 'is-active' : ''}`}
                onClick={() => setReason(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        ) : null}
      </form>
    </Modal>
  );
};
